import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGetPreviousQuestionQuery } from '../../api/ester-b2b-api/private/screening/screeningQueries'
import { useScreeningQuestionnaireStore } from '../../state/store'
import { queryErrorHandlerWithMessage } from '../../utils/queryErrorHandler'

type Params = {
  screeningId: string | undefined
  /** Where the questionnaire is rendered, for steps that sit on their own route after it. */
  questionsPath: string
  /** Shown when the request succeeds but hands back no question. */
  fallbackMessage: string
  onError: (message: string) => void
}

/**
 * Steps a questionnaire back one question and returns to it, so the steps that follow the last
 * question can be backed out of the same way questions can.
 */
export const useGoToPreviousQuestion = ({
  screeningId,
  questionsPath,
  fallbackMessage,
  onError,
}: Params): (() => Promise<void>) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setCurrentQuestion = useScreeningQuestionnaireStore((state) => state.setCurrentQuestion)
  const { refetch: getPreviousQuestion } = useGetPreviousQuestionQuery(screeningId)

  return useCallback(async () => {
    const { data: previousQuestion, error } = await getPreviousQuestion()

    if (previousQuestion) {
      setCurrentQuestion(previousQuestion)
      navigate(questionsPath)
      return
    }

    onError(error ? queryErrorHandlerWithMessage('get previous question', t)(error) : fallbackMessage)
  }, [fallbackMessage, getPreviousQuestion, navigate, onError, questionsPath, setCurrentQuestion, t])
}
